import moonbugLogo from "./Platforms&Music_Logo.png";
import { useDispatch, useSelector } from "react-redux";
import { MenuItem } from "primereact/menuitem";
import { Button } from "primereact/button";
import { useRef, useState, MouseEvent, useEffect } from "react";
import { auth } from "../../Features/config/firebase";
import React from "react";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../config/routes";
import { signOut } from "../../Features/Auth/AuthenticationSlice";
import { Sidebar } from "primereact/sidebar";
import { classNames } from "primereact/utils";
import { Menu } from "primereact/menu";
import { Ripple } from "primereact/ripple";
import { Tree } from "primereact/tree";
import { InputSwitch } from "primereact/inputswitch";
import { useTheme } from "../../hooks/useTheme";

// Import centralized navigation configuration
import {
  getNavigationMenuItems,
  UserRoles,
  MenuItemWithHeading,
} from "../../config/routes";

import { useUserAccess } from "../../hooks/useUserAccess";
import { hasActiveOverrides } from "../../utils/devOverrides";
import { getDevPreviewRole } from "../../utils/userAccess";

export interface HomeMenuBarProps {
  title?: string | React.ReactNode;
  Tabs?: React.ReactNode;
  onSidebarVisibilityChange?: (isVisible: boolean) => void;
}

const HomeMenuBar: React.FC<HomeMenuBarProps> = ({
  title,
  Tabs,
  onSidebarVisibilityChange,
}) => {
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const userMenu = useRef<any>(null);
  const dispatch = useDispatch();
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);
  const navigate = useNavigate();
  const user = useSelector((state: any) => state.googleAuth.user);
  const { isDarkMode, toggleTheme } = useTheme();

  useEffect(() => {
    onSidebarVisibilityChange?.(sidebarVisible);
  }, [sidebarVisible, onSidebarVisibilityChange]);

  const handleImageClick = (event: MouseEvent<HTMLImageElement>) => {
    userMenu.current?.toggle(event);
  };

  const handleMenuOpen = () => {
    setSidebarVisible(!sidebarVisible);
  };

  const signOutWithGoogle = async () => {
    await auth.signOut();
    dispatch(signOut());
    navigate(ROUTES.ROOT);
  };

  // Resolved role/tools (incl. dev overrides and the localhost role preview)
  const userAccess = useUserAccess();
  const userRoles: UserRoles = React.useMemo(
    () => ({ role: userAccess.role, tools: userAccess.tools }),
    [userAccess.role, userAccess.tools]
  );

  // Generate menu items using unified route configuration
  const navigationMenuItems = getNavigationMenuItems(userRoles, navigate);

  // Convert to MenuItemWithHeading format for the sidebar
  const menuItems: MenuItemWithHeading[] = [
    ...navigationMenuItems,
    {
      template: () => {
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              paddingLeft: "1%",
              bottom: "0px",
            }}
          >
            <UserActions />
          </div>
        );
      },
    },
  ];

  const userMenuItems: MenuItem[] = [
    {
      label: "Log Out",
      icon: "pi pi-sign-out",
      command: signOutWithGoogle,
    },
  ];

  // Helper function to determine indentation level
  const getIndentationLevel = (label: string | undefined): number => {
    if (!label) return 0;
    const spaces = label.match(/^(\s*)/)?.[1]?.length || 0;
    return Math.floor(spaces / 2); // Each level is 2 spaces
  };

  const flattenMenuItems = (
    items: MenuItemWithHeading[],
    level: number = 0
  ): MenuItemWithHeading[] => {
    const flattened: MenuItemWithHeading[] = [];

    items.forEach((item) => {
      if (Array.isArray(item.items)) {
        // Add the parent item
        if (!item.separator && item.label && !item.template) {
          const indentedItem = {
            ...item,
            label: level > 0 ? `${"".repeat(level)}${item.label}` : item.label,
          };
          flattened.push(indentedItem as MenuItemWithHeading);
        }

        // Recursively add submenu items
        const subItems = flattenMenuItems(
          item.items as MenuItemWithHeading[],
          level + 1
        );
        flattened.push(...subItems);
      } else if (!item.separator && item.label && !item.template) {
        const indentedItem = {
          ...item,
          label: level > 0 ? `${"  ".repeat(level)}${item.label}` : item.label,
        };
        flattened.push(indentedItem as MenuItemWithHeading);
      }
    });

    return flattened;
  };

  const filteredMenuItems = menuItems.filter(
    (item) => Object.keys(item).length > 0 && !item.template
  );
  const flattenedMenuItems = flattenMenuItems(filteredMenuItems);
  useEffect(() => {
    if (!sidebarVisible) {
      setSelectedItem(null);
    }
  }, [sidebarVisible]);

  const LeftSide = () => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        width: "84%",
        height: "100%",
        left: "0px",
        zIndex: 1000,
      }}
    >
      <Button
        icon={sidebarVisible ? "pi pi-chevron-right" : "pi pi-bars"}
        onClick={handleMenuOpen}
        aria-controls="popup_menu_left"
        aria-haspopup
        text
        size="large"
        style={{
          height: "100%",
          width: "auto",
          zIndex: 1000,
          padding: "0.25rem",
          paddingLeft: "1%",
        }}
      />
      <Sidebar
        onHide={handleMenuOpen}
        visible={sidebarVisible}
        content={({ hide }) => {
          return (
            <div className="min-h-screen flex relative lg:static surface-ground">
              <div
                id="app-sidebar-2"
                className="surface-section h-screen block flex-shrink-0 absolute lg:static left-0 top-0 z-1 border-right-1 surface-border select-none"
              >
                <div className="flex flex-column h-full">
                  <div className="flex align-items-center justify-content-between px-3 pt-2 pb-1 flex-shrink-0">
                    <span
                      className="font-semibold text-2xl"
                      style={{ color: "#2a3240" }}
                    >
                      {/* <text >Tool Suite</text> */}
                    </span>
                    <span>
                      <Button
                        type="button"
                        onClick={(e) => hide(e)}
                        icon="pi pi-chevron-left"
                        rounded
                        text
                        size="small"
                        className="h-1.5rem w-1.5rem"
                      ></Button>
                    </span>
                  </div>
                  <div className="overflow-y-auto">
                    <ul className="list-none p-2 m-0 font-small">
                      {flattenedMenuItems.map((item, index) => (
                        <React.Fragment key={index}>
                          {item.heading && (
                            <li
                              className="sidebar-heading font-medium text-sm"
                              key={`heading-${index}`}
                            >
                              {item.heading}
                              <hr className="mb-2 mx-0 border-top-1 border-none surface-border" />
                            </li>
                          )}
                          <li
                            className={classNames("sidebar-menu-item", {
                              "sidebar-menu-item-selected":
                                selectedItem === item,
                            })}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (item.command) {
                                item.command({ originalEvent: e, item: item });
                              }
                              setSidebarVisible(false);
                              setSelectedItem(item);
                            }}
                            style={{
                              marginBottom: "0.5rem",
                              marginLeft:
                                getIndentationLevel(item.label) > 0
                                  ? `${
                                      0.75 +
                                      (getIndentationLevel(item.label) - 1) *
                                        0.5
                                    }rem`
                                  : "-0.75rem",
                              paddingLeft:
                                getIndentationLevel(item.label) > 0
                                  ? "0.75rem"
                                  : "0",
                              borderLeft:
                                getIndentationLevel(item.label) > 0
                                  ? "2px solid #e5e7eb"
                                  : "none",
                            }}
                          >
                            <button className="p-ripple flex align-items-center cursor-pointer p-1 border-round text-700 hover:surface-100 transition-duration-150 transition-colors w-full border-none bg-transparent">
                              <div className="p-ripple p-2 flex align-items-center justify-content-between text-600 cursor-pointer">
                                <i
                                  className={classNames(
                                    item.icon,
                                    "sidebar-menu-icon",
                                    getIndentationLevel(item.label) > 0
                                      ? "text-sm"
                                      : ""
                                  )}
                                />
                              </div>

                              <span
                                className={classNames("sidebar-menu-label", {
                                  "text-sm":
                                    getIndentationLevel(item.label) > 0,
                                  "font-medium":
                                    getIndentationLevel(item.label) === 0,
                                })}
                              >
                                {item.label}
                              </span>
                              <Ripple />
                            </button>
                          </li>
                        </React.Fragment>
                      ))}
                    </ul>
                  </div>
                  <div className="mt-auto">
                    <div className="sidebar-preferences px-3 pb-2">
                      <div className="sidebar-heading font-medium text-sm">
                        Preferences
                        <hr className="mb-2 mx-0 border-top-1 border-none surface-border" />
                      </div>
                      <label
                        className="flex align-items-center justify-content-between gap-3 cursor-pointer text-700"
                        htmlFor="sidebar-dark-mode"
                      >
                        <span className="flex align-items-center gap-2 text-sm">
                          <i className={isDarkMode ? "pi pi-moon" : "pi pi-sun"} aria-hidden />
                          Dark mode
                        </span>
                        <InputSwitch
                          inputId="sidebar-dark-mode"
                          checked={isDarkMode}
                          onChange={() => toggleTheme()}
                        />
                      </label>
                    </div>
                    <button className="m-2 flex align-items-center cursor-pointer p-1 gap-1 border-round text-700 hover:surface-100 transition-duration-150 transition-colors p-ripple border-none bg-transparent">
                      <UserActions />
                      <span>
                        <Button
                          onClick={(e) => handleImageClick(e as any)}
                          icon="pi pi-ellipsis-v"
                          text
                          rounded
                          size="small"
                          severity="secondary"
                          className="h-1.5rem w-1.5rem"
                        />
                      </span>
                      <span className="font-bold"></span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        }}
        style={{
          bottom: "0px",
          alignItems: "end",
          width: "auto",
          paddingLeft: "1%",
          zIndex: 1001,
        }}
        position="left"
      />

      <h3 className="app-menubar-title"
        style={{
          color: "#374151",
          fontSize: "1.1rem",
          margin: "0 1%",
        }}
      >
        {title}
        {(hasActiveOverrides(user?.email) || !!getDevPreviewRole(user?.email)) && (
          <span
            style={{
              marginLeft: "8px",
              fontSize: "10px",
              backgroundColor: "#f59e0b",
              color: "white",
              padding: "1px 4px",
              borderRadius: "3px",
              fontWeight: "bold",
            }}
          >
            DEV
          </span>
        )}
      </h3>
      {Tabs}
    </div>
  );

  const UserActions = () => (
    <div className="flex align-items-center" style={{ bottom: "0px" }}>
      <img
        src={user.photoURL}
        alt={user.displayName}
        style={{
          width: "24px",
          height: "24px",
          borderRadius: "50%",
          cursor: "pointer",
        }}
      />
      <span
        className="app-menubar-user"
        style={{
          marginLeft: "8px",
          color: "#374151",
          fontSize: "0.9rem",
          fontWeight: "500",
        }}
      >
        {user.displayName}
      </span>
      <Menu model={userMenuItems} popup ref={userMenu} id="popup_menu_user" />
    </div>
  );

  return (
    <div className="app-menubar"
      style={{
        display: "flex",
        justifyContent: "start",
        alignItems: "center",
        backgroundColor: "white",
        width: "100%",
        height: "7vh",
        maxHeight: "60px",
        zIndex: 1000,
        position: "sticky",
        top: 0,
      }}
    >
      <LeftSide />
      <img
        alt="Platforms & Music – home"
        src={moonbugLogo}
        onClick={() => navigate(ROUTES.HOMEPAGE)}
        style={{ justifySelf: "end", width: "auto", height: "60%", cursor: "pointer" }}
      ></img>
    </div>
  );
};

export default HomeMenuBar;
